import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import api from "../services/api";
import { useAuth } from "./AuthContext";
import {
  pinMessage,
  unpinMessage,
  getPinnedMessages,
} from "../services/pinnedMessages";
import {
  getCanvas,
  addCanvasBlock as addCanvasBlockApi,
  updateCanvasBlock as updateCanvasBlockApi,
  deleteCanvasBlock as deleteCanvasBlockApi,
} from "../services/canvasService";
import { getMessageByChannelId } from "../services/channelService";
import { getChats } from "../services/chatServices";
import { getAllDrafts } from "../services/draftService";
import { normalizeChannelMessages } from "../utils/messageNormalizer";

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();

  // ── Stable primitive ID — the only dep that should ever trigger re-fetches ──
  const userId = (user?._id || user?.id) ?? null;

  const [channels, setChannels] = useState([]);
  const [directMessages, setDirectMessages] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [targetMessageId, setTargetMessageId] = useState(null);
  const [highlightQuery, setHighlightQuery] = useState(null);
  const [dmUnreadCounts, setDmUnreadCounts] = useState({});
  const [channelUnreadCounts, setChannelUnreadCounts] = useState({});
  const [totalUnread, setTotalUnread] = useState(0);
  const [lastReadAt, setLastReadAt] = useState({}); // { chatId: timestamp }
  const [pinnedMessages, setPinnedMessages] = useState({});
  const [pinnedMessagesCounts, setPinnedMessagesCounts] = useState({});
  const [canvasByConversation, setCanvasByConversation] = useState({}); // { conversationId: blocks[] }
  const [channelFiles, setChannelFiles] = useState({});
  const [draftsStatus, setDraftsStatus] = useState({});
  const [userDrafts, setUserDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [showThreadsModal, setShowThreadsModal] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [selectedProfileUserId, setSelectedProfileUserId] = useState(null);

  // Guard ref — prevents concurrent duplicate draft fetches
  const isFetchingDraftsRef = useRef(false);
  // Expose draftsLoading as a ref too so context consumers don't re-render on loading state changes
  const draftsLoadingRef = useRef(false);

  const updateChatDraftStatus = useCallback((chatId, hasDraft) => {
    if (!chatId) return;
    setDraftsStatus((prev) => ({ ...prev, [chatId]: hasDraft }));
  }, []);

  // ── refetchDrafts ─────────────────────────────────────────────────────────
  // Depends only on the stable userId string, NOT the user object.
  // This means it is created exactly once per login session and never
  // causes refreshData (or anything else) to be recreated.
  const refetchDrafts = useCallback(async () => {
    if (!userId) {
      setUserDrafts([]);
      setDraftsLoading(false);
      return;
    }
    // Concurrent-call guard — if a fetch is already in flight, skip
    if (isFetchingDraftsRef.current) return;
    isFetchingDraftsRef.current = true;
    setDraftsLoading(true);
    draftsLoadingRef.current = true;
    try {
      const response = await getAllDrafts();
      setUserDrafts(response?.success ? response.data || [] : []);
    } catch (error) {
      console.error("[ChatContext] ❌ Error fetching drafts:", error.message);
      setUserDrafts([]);
    } finally {
      setDraftsLoading(false);
      draftsLoadingRef.current = false;
      isFetchingDraftsRef.current = false;
    }
  }, [userId]); // ← string, not object

  // ── Fetch drafts ONCE on login — completely independent of refreshData ────
  useEffect(() => {
    refetchDrafts();
  }, [refetchDrafts]); // stable: only re-runs when userId changes (login/logout)

  // ── Pinned Messages ───────────────────────────────────────────────────────
  const fetchPinnedMessages = useCallback(async (channelId) => {
    try {
      const response = await getPinnedMessages(channelId);
      if (response?.data) {
        const normalized = normalizeChannelMessages(response.data);
        setPinnedMessages((prev) => ({ ...prev, [channelId]: normalized }));
        setPinnedMessagesCounts((prev) => ({
          ...prev,
          [channelId]: normalized.length,
        }));
      }
    } catch (error) {
      console.error("Error fetching pinned messages:", error);
    }
  }, []);

  // ── Canvas (per-conversation, block-ownership document) ───────────────────
  const fetchCanvas = useCallback(async (conversationId) => {
    if (!conversationId) return;
    try {
      const response = await getCanvas(conversationId);
      const blocks = response?.data?.blocks || [];
      setCanvasByConversation((prev) => ({ ...prev, [conversationId]: blocks }));
    } catch (error) {
      console.error("Error fetching canvas:", error);
    }
  }, []);

  // Apply blocks pushed from the server (socket canvas:update).
  const applyCanvasBlocks = useCallback((conversationId, blocks) => {
    if (!conversationId) return;
    setCanvasByConversation((prev) => ({
      ...prev,
      [conversationId]: Array.isArray(blocks) ? blocks : [],
    }));
  }, []);

  const addCanvasBlock = useCallback(
    async (conversationId, content, chatType) => {
      const response = await addCanvasBlockApi(conversationId, content, chatType);
      const blocks = response?.data?.blocks || [];
      setCanvasByConversation((prev) => ({ ...prev, [conversationId]: blocks }));
      return blocks;
    },
    [],
  );

  const updateCanvasBlock = useCallback(
    async (conversationId, blockId, content) => {
      const response = await updateCanvasBlockApi(conversationId, blockId, content);
      const blocks = response?.data?.blocks || [];
      setCanvasByConversation((prev) => ({ ...prev, [conversationId]: blocks }));
      return blocks;
    },
    [],
  );

  const deleteCanvasBlock = useCallback(
    async (conversationId, blockId) => {
      const response = await deleteCanvasBlockApi(conversationId, blockId);
      const blocks = response?.data?.blocks || [];
      setCanvasByConversation((prev) => ({ ...prev, [conversationId]: blocks }));
      return blocks;
    },
    [],
  );

  const fetchChannelFiles = useCallback(
    async (
      conversationId,
      conversationType = "channel",
      otherUserId = null,
    ) => {
      try {
        let files = [];
        if (conversationType === "channel") {
          const response = await getMessageByChannelId(conversationId);
          if (response?.data) {
            files = response.data.filter(
              (msg) =>
                msg &&
                (msg.type === "url" ||
                  msg.type === "file" ||
                  msg.type === "image"),
            );
          }
        } else if (conversationType === "dm" && otherUserId) {
          if (userId) {
            const response = await getChats(userId, otherUserId);
            if (response?.data) {
              files = response.data.filter(
                (msg) =>
                  msg &&
                  (msg.type === "url" ||
                    msg.type === "file" ||
                    msg.type === "image"),
              );
            }
          }
        }
        setChannelFiles((prev) => ({ ...prev, [conversationId]: files }));
      } catch (error) {
        console.error("Error fetching conversation files:", error);
      }
    },
    [userId],
  );

  const handlePinMessage = useCallback(
    async (messageId, channelId) => {
      if (!userId) {
        console.error("handlePinMessage: no userId");
        return false;
      }
      try {
        await pinMessage(messageId, channelId, userId);
        await fetchPinnedMessages(channelId);
        return true;
      } catch (error) {
        console.error("Error pinning message:", error);
        return false;
      }
    },
    [fetchPinnedMessages, userId],
  );

  const handleUnpinMessage = useCallback(
    async (messageId, channelId) => {
      try {
        await unpinMessage(messageId, channelId, userId);
        await fetchPinnedMessages(channelId);
        return true;
      } catch (error) {
        console.error("Error unpinning message:", error);
        return false;
      }
    },
    [fetchPinnedMessages, userId],
  );

  // ── refreshData ───────────────────────────────────────────────────────────
  // Depends only on userId (string). Does NOT call refetchDrafts — drafts
  // have their own independent effect above. This breaks the loop:
  //   old: refreshData → refetchDrafts → setDraftsLoading → re-render →
  //        new refetchDrafts ref → new refreshData ref → useEffect fires again
  //   new: refreshData is stable for the entire login session.
  const refreshData = useCallback(async () => {
    if (!userId) return;

    try {
      const [channelsRes, unreadRess, channelUnreadRes] = await Promise.all([
        api.get("/channels"),
        api.get(`/get-all-employees/${userId}`),
        api.get(`/get-unReadCount/${userId}`),
      ]);

      const channelsData =
        channelsRes?.data || (Array.isArray(channelsRes) ? channelsRes : []);
      console.log(
        "ChatContext | refreshData | channels:",
        channelsData.length,
        "| employees raw count:",
        (unreadRess?.data || unreadRess)?.length ?? 0,
      );
      setChannels(channelsData);

      setActiveChat((prev) => {
        if (prev?.type === "channel") {
          const id = prev.data._id || prev.data.id;
          const fresh = channelsData.find((c) => (c._id || c.id) === id);
          if (fresh) return { ...prev, data: fresh };
        }
        return prev;
      });

      const userMap = {};
      channelsData.forEach((ch) => {
        (ch.members || []).forEach((m) => {
          const mId = m._id || m.id;
          if (mId && mId !== userId && !userMap[mId]) {
            userMap[mId] = { ...m, unreadCount: 0 };
          }
        });
      });

      const statsData =
        unreadRess?.data || (Array.isArray(unreadRess) ? unreadRess : []);
      const dmCounts = {};
      let total = 0;

      statsData.forEach((stat) => {
        const sid = stat.otherUserId;
        const count = stat.unreadCount || 0;
        if (sid && sid !== userId) {
          dmCounts[sid] = count;
          total += count;
          if (userMap[sid]) {
            userMap[sid].unreadCount = count;
          } else if (stat.otherUserData) {
            userMap[sid] = { ...stat.otherUserData, unreadCount: count };
          }
        }
      });

      const finalDMs = Object.values(userMap).sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      );
      setDirectMessages(finalDMs);

      const searchUserMap = {};
      statsData.forEach((item) => {
        const uid = item.otherUserId;
        if (!uid) return;
        if (!searchUserMap[uid]) {
          searchUserMap[uid] = {
            _id: uid,
            name: item.otherUserData?.name || "Unknown",
            email: item.otherUserData?.email || "",
            profilePhoto: item.otherUserData?.profilePhoto || null,
            isLive: item.otherUserData?.isLive || false,
            unreadCount: item.unreadCount || 0,
          };
        } else {
          searchUserMap[uid].unreadCount += item.unreadCount || 0;
          if (item.otherUserData?.isLive) searchUserMap[uid].isLive = true;
        }
      });
      setUsersData(
        Object.values(searchUserMap).sort((a, b) =>
          (a.name || "").localeCompare(b.name || ""),
        ),
      );

      setActiveChat((prev) => {
        if (prev?.type === "dm") {
          const otherUserId = prev.data._id || prev.data.id;
          const freshDM = finalDMs.find(
            (dm) => (dm._id || dm.id) === otherUserId,
          );
          if (freshDM) return { ...prev, data: freshDM };
        }
        return prev;
      });

      // Channel unread counts (per-user, from seenBy aggregation)
      const channelUnreadData = channelUnreadRes?.data || {};

      // setDmUnreadCounts(prev => ({ ...prev, ...dmCounts }));
      // setChannelUnreadCounts(prev => ({ ...prev, ...channelUnreadData }));
      // setTotalUnread(total + Object.values(channelUnreadData).reduce((s, v) => s + (v || 0), 0));

      //=================================
      setDmUnreadCounts((prev) => {
        const merged = { ...prev };
        Object.entries(dmCounts).forEach(([id, serverCount]) => {
          merged[id] = Math.max(prev[id] || 0, serverCount || 0);
        });
        return merged;
      });
      setChannelUnreadCounts((prev) => {
        const merged = { ...prev };
        Object.entries(channelUnreadData).forEach(([id, serverCount]) => {
          merged[id] = Math.max(prev[id] || 0, serverCount || 0);
        });
        return merged;
      });
      // Recompute total from server counts only (notification:receive handles real-time deltas)
      setTotalUnread(
        Object.values({ ...dmCounts }).reduce((s, v) => s + (v || 0), 0) +
          Object.values(channelUnreadData).reduce((s, v) => s + (v || 0), 0),
      );
      //=================================
    } catch (error) {
      console.error(
        "ChatContext | refreshData failed:",
        error.message,
        "| status:",
        error.response?.status,
        "| url:",
        error.config?.url,
      );
    }
    // ⚠️ No refetchDrafts call here — drafts have their own effect.
  }, [userId]); // ← only the stable string

  // ── Initial data load — fires once per login session ─────────────────────
  useEffect(() => {
    refreshData();
  }, [refreshData]); // stable: only re-runs when userId changes

  const selectChat = useCallback((type, data, preserveSearch = false) => {
    setActiveChat({ type, data });
    if (!preserveSearch) {
      setHighlightQuery(null);
      setTargetMessageId(null);
    }
  }, []);

  // ── Profile Modal Functions ─────────────────────────────────────────────────────
  const openUserProfile = useCallback((userId) => {
    setSelectedProfileUserId(userId);
    setShowUserProfileModal(true);
  }, []);

  const closeUserProfile = useCallback(() => {
    setShowUserProfileModal(false);
    setSelectedProfileUserId(null);
  }, []);
 
  // ── Message Editing ───────────────────────────────────────────────────────
  const updateChannelMessage = useCallback(async (messageId, content) => {
    try {
      const response = await api.put(`/channels/messages/${messageId}`, {
        content,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating channel message:", error);
      throw error;
    }
  }, []);

  const updateDirectMessage = useCallback(
    async (chatUniqueId, messageId, content) => {
      try {
        const response = await api.put(`/chat/messages/${messageId}`, {
          uniqueId: chatUniqueId,
          content,
        });
        return response.data;
      } catch (error) {
        console.error("Error updating direct message:", error);
        throw error;
      }
    },
    [],
  );

  // ── markChatAsRead ────────────────────────────────────────────────────────
  // Called when the user opens a chat — resets unread count to 0 in local state.
  // The server-side unread count is reset separately via markAsRead API in ChatPanel.
  const markChatAsRead = useCallback((chatId, type, timestamp) => {
    if (!chatId || !type) return;

    const readAt = timestamp ? new Date(timestamp).getTime() : Date.now();

    // Strict Source of Truth: Only update if the new read timestamp is greater than existing
    setLastReadAt((prev) => {
      if (prev[chatId] && prev[chatId] >= readAt) return prev;
      return { ...prev, [chatId]: readAt };
    });

    if (type === "dm") {
      setDmUnreadCounts((prev) => {
        if (prev[chatId] === 0) return prev; // already 0, skip re-render
        const diff = prev[chatId] || 0;
        const next = { ...prev, [chatId]: 0 };
        setTotalUnread((prevTotal) => Math.max(0, prevTotal - diff));
        return next;
      });
    } else if (type === "channel") {
      setChannelUnreadCounts((prev) => {
        if (prev[chatId] === 0) return prev; // already 0, skip re-render
        const diff = prev[chatId] || 0;
        const next = { ...prev, [chatId]: 0 };
        setTotalUnread((prevTotal) => Math.max(0, prevTotal - diff));
        return next;
      });
    }
  }, []);

  // ── Memoized context value ────────────────────────────────────────────────
  // IMPORTANT: draftsLoading is intentionally excluded from the useMemo deps.
  // It is a transient loading flag — including it would cause every useChat()
  // consumer to re-render (and re-run their useEffects) twice per draft fetch:
  // once when loading starts and once when it ends. That's what was causing
  // useDraft's main effect to re-run and call getDraftAPI repeatedly.
  // Consumers that need the loading state should read it directly; it will
  // still update correctly in the context value on the next meaningful render.
  const contextValue = useMemo(
    () => ({
      channels,
      directMessages,
      usersData,
      activeChat,
      targetMessageId,
      setTargetMessageId,
      highlightQuery,
      setHighlightQuery,
      dmUnreadCounts,
      channelUnreadCounts,
      totalUnread,
      pinnedMessages,
      pinnedMessagesCounts,
      channelFiles,
      fetchChannelFiles,
      refreshData,
      selectChat,
      setDmUnreadCounts,
      setChannelUnreadCounts,
      setTotalUnread,
      setDirectMessages,
      fetchPinnedMessages,
      handlePinMessage,
      handleUnpinMessage,
      canvasByConversation,
      fetchCanvas,
      applyCanvasBlocks,
      addCanvasBlock,
      updateCanvasBlock,
      deleteCanvasBlock,
      draftsStatus,
      updateChatDraftStatus,
      userDrafts,
      draftsLoading,
      refetchDrafts,
      showDraftsModal,
      setShowDraftsModal,
      showThreadsModal,
      setShowThreadsModal,
      onlineUsers,
      setOnlineUsers,
      markChatAsRead,
      lastReadAt,
      setLastReadAt,
      showUserProfileModal,
      setShowUserProfileModal,
      selectedProfileUserId,
      setSelectedProfileUserId,
      openUserProfile,
      closeUserProfile,
      updateChannelMessage,
      updateDirectMessage,
    }),
    [
      channels,
      directMessages,
      usersData,
      activeChat,
      targetMessageId,
      highlightQuery,
      dmUnreadCounts,
      channelUnreadCounts,
      totalUnread,
      pinnedMessages,
      pinnedMessagesCounts,
      channelFiles,
      fetchChannelFiles,
      refreshData,
      selectChat,
      fetchPinnedMessages,
      handlePinMessage,
      handleUnpinMessage,
      canvasByConversation,
      fetchCanvas,
      applyCanvasBlocks,
      addCanvasBlock,
      updateCanvasBlock,
      deleteCanvasBlock,
      draftsStatus,
      updateChatDraftStatus,
      userDrafts,
      refetchDrafts,
      showDraftsModal,
      showThreadsModal,
      onlineUsers,
      markChatAsRead,
      lastReadAt,
      showUserProfileModal,
      selectedProfileUserId,
      openUserProfile,
      closeUserProfile,
      updateChannelMessage,
      updateDirectMessage,
    ],
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};
